import React from 'react';
import { createPortal } from 'react-dom';
import './OrderDetailModal.css';
import { Order, OrderStatus } from '../../services/api';
import { useModalA11y } from '../../hooks/useModalA11y';
import { toast } from '../Toast/Toast';

interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'status-pending',
  paid: 'status-paid',
  processing: 'status-paid',
  shipped: 'status-shipped',
  delivered: 'status-delivered',
  cancelled: 'status-cancelled',
  refunded: 'status-refunded',
};

function dollars(cents: number | undefined, fallback?: number): string {
  if (typeof cents === 'number') return `$${(cents / 100).toFixed(2)}`;
  return `$${Number(fallback || 0).toFixed(2)}`;
}

function copyToClipboard(text: string, label: string) {
  if (!navigator?.clipboard) {
    toast.error('Clipboard unavailable.');
    return;
  }
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied.`),
    () => toast.error('Copy failed.'),
  );
}

interface AddressLike {
  name?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  email?: string;
  phone?: string;
}

function parseAddress(raw: string | null | undefined): AddressLike | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, onClose }) => {
  const dialogRef = useModalA11y({ onEscape: onClose });

  const address = parseAddress(order.shipping_address_json || order.shipping_address);

  return createPortal((
    <div className="modal-overlay order-detail-overlay" onClick={onClose} ref={dialogRef} role="dialog" aria-modal="true" aria-label="Order detail" tabIndex={-1}>
      <div className="order-detail-modal animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        <div className="order-detail-header">
          <div className="order-detail-title">
            <span className="order-detail-id">Order #{order.id.slice(-8)}</span>
            <span className={`status-pill ${STATUS_COLORS[order.status] || 'status-pending'}`}>
              {order.status}
            </span>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="order-detail-body">
          <section className="order-section">
            <h4>Shipping</h4>
            {address ? (
              <div className="address-block">
                {address.name && <div className="address-name">{address.name}</div>}
                <div>{address.street}{address.street2 ? `, ${address.street2}` : ''}</div>
                <div>
                  {[address.city, address.state, address.zip].filter(Boolean).join(', ')}
                </div>
                {address.country && <div>{address.country}</div>}
                {address.email && <div className="address-contact">{address.email}</div>}
                {address.phone && <div className="address-contact">{address.phone}</div>}
                {/* Quick links — pre-paste the address into the maps app
                    and copy a single-line version for shipping labels. */}
                <div className="address-actions">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([
                      address.street,
                      [address.city, address.state, address.zip].filter(Boolean).join(' '),
                      address.country,
                    ].filter(Boolean).join(', '))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-copy-btn"
                    title="Open this address in Google Maps"
                  >
                    Open in Maps
                  </a>
                  <button
                    className="inline-copy-btn"
                    onClick={() => copyToClipboard(
                      [
                        address.name,
                        address.street + (address.street2 ? `, ${address.street2}` : ''),
                        [address.city, address.state, address.zip].filter(Boolean).join(', '),
                        address.country,
                      ].filter(Boolean).join('\n'),
                      'Shipping address',
                    )}
                  >Copy address</button>
                </div>
              </div>
            ) : (
              <div className="empty-note">Address not yet captured.</div>
            )}
            {order.tracking_number && (
              <div className="tracking-row">
                <span className="tracking-label">Tracking</span>
                <code className="tracking-code">{order.tracking_number}</code>
                <button
                  className="inline-copy-btn"
                  onClick={() => copyToClipboard(order.tracking_number || '', 'Tracking number')}
                >Copy</button>
              </div>
            )}
          </section>

          <section className="order-section">
            <h4>Items</h4>
            <div className="order-items">
              {(order.items || []).map(item => {
                const unit = typeof item.unit_price_cents === 'number'
                  ? item.unit_price_cents / 100
                  : Number(item.price || 0);
                return (
                  <div className="order-item" key={(item.id || item.product_id) + (item.size || '')}>
                    {item.product_image ? (
                      <img
                        src={item.product_image}
                        alt=""
                        className="order-item-image"
                        loading="lazy"
                        onError={e => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          const ph = document.createElement('div');
                          ph.className = 'order-item-image placeholder';
                          img.parentElement?.insertBefore(ph, img);
                        }}
                      />
                    ) : (
                      <div className="order-item-image placeholder" />
                    )}
                    <div className="order-item-info">
                      <div className="order-item-name">{item.product_name}</div>
                      {item.size && <div className="order-item-meta">Size {item.size}</div>}
                      <div className="order-item-meta">
                        Qty {item.quantity} × ${unit.toFixed(2)}
                      </div>
                    </div>
                    <div className="order-item-line">
                      ${(unit * item.quantity).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="order-section totals">
            <div className="totals-row"><span>Subtotal</span><span>{dollars(order.subtotal_cents, order.subtotal)}</span></div>
            <div className="totals-row"><span>Shipping</span><span>{dollars(order.shipping_cents, order.shipping)}</span></div>
            <div className="totals-row"><span>Tax</span><span>{dollars(order.tax_cents, order.tax)}</span></div>
            <div className="totals-row total"><span>Total</span><span>{dollars(order.total_cents, order.total)}</span></div>
          </section>

          <section className="order-section">
            <h4>Payment</h4>
            <div className="payment-row">
              <span className="payment-label">PayPal Order ID</span>
              {order.paypal_order_id ? (
                <>
                  <code className="payment-value">{order.paypal_order_id}</code>
                  <button className="inline-copy-btn" onClick={() => copyToClipboard(order.paypal_order_id || '', 'PayPal Order ID')}>Copy</button>
                </>
              ) : (
                <span className="empty-note">—</span>
              )}
            </div>
            <div className="payment-row">
              <span className="payment-label">PayPal Capture ID</span>
              {order.paypal_capture_id ? (
                <>
                  <code className="payment-value">{order.paypal_capture_id}</code>
                  <button className="inline-copy-btn" onClick={() => copyToClipboard(order.paypal_capture_id || '', 'Capture ID')}>Copy</button>
                </>
              ) : (
                <span className="empty-note">—</span>
              )}
            </div>
          </section>

          <section className="order-section meta-section">
            <div className="meta-row"><span>Created</span><span>{new Date(order.created_at).toLocaleString()}</span></div>
            {order.updated_at && (
              <div className="meta-row"><span>Updated</span><span>{new Date(order.updated_at).toLocaleString()}</span></div>
            )}
            <div className="meta-row">
              <span>Buyer ID</span>
              <code>{order.user_id}</code>
              <button className="inline-copy-btn" onClick={() => copyToClipboard(order.user_id, 'Buyer ID')}>Copy</button>
            </div>
          </section>
        </div>
      </div>
    </div>
  ), document.body);
};

export default OrderDetailModal;
